/**
 * Tiny fetch wrapper that attaches the Cognito JWT to every Flask API
 * call. Lives here (in `lib/`) so any feature can import it without
 * crossing feature boundaries. See docs/auth.md for the contract.
 *
 * 401 contract
 * ------------
 * When Flask returns 401, the wrapper assumes the access/ID token has
 * expired. It calls `fetchAuthSession({ forceRefresh: true })` to spin
 * the refresh token through Cognito, then retries the original request
 * once with the fresh token. The user is signed out (and redirected to
 * `/` with `?reason=session-expired`) ONLY when the session is provably
 * dead: Cognito rejects the refresh token (`NotAuthorizedException`),
 * there is no session at all, or the retry with a fresh token still
 * comes back 401.
 *
 * A refresh that fails for any OTHER reason (network blip, laptop
 * waking from sleep, Cognito throttling) must NOT sign the user out:
 * `signOut()` revokes the refresh token server-side (the App Client
 * has token revocation enabled), which would turn a one-off transient
 * failure into a permanent logout. Those failures surface as a plain
 * ApiError(401) and the session stays intact for the next attempt.
 *
 * The retry is bounded by the function's straight-line code (no
 * recursion, no loop), so we deliberately do NOT send a "I already
 * retried" header on the wire — that would force the browser into a
 * second CORS preflight for a header Flask doesn't even read.
 */
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

import { env } from './env';

const SESSION_EXPIRED_REDIRECT = '/?reason=session-expired';

/** Machine-readable error body shape Flask returns (see app.core.schemas). */
export interface ApiErrorBody {
  error?: string;
  message?: string;
  code?: string;
  feature?: string;
  required_plan?: string | null;
  // INSUFFICIENT_CREDITS (credit metering): the upgrade target to offer, null
  // at the top tier; plus how many credits the op needed vs the balance.
  next_plan?: string | null;
  credits_required?: number;
  credits_available?: number;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message: string,
    /** The parsed JSON error body, when the response carried one. */
    public readonly body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True for a 402 plan-gate response (`code === "UPGRADE_REQUIRED"`). */
  get isUpgradeRequired(): boolean {
    return this.status === 402 && this.body?.code === 'UPGRADE_REQUIRED';
  }
}

/**
 * Hard ceiling on how long Amplify's token plumbing may run.
 *
 * `fetchAuthSession` can HANG (not reject) when a Cognito refresh
 * stalls — a known Amplify failure mode. Every API call awaits the
 * token BEFORE fetching, so a hung refresh used to freeze requests
 * silently: endless spinner, zero network traffic, nothing in the
 * backend logs (seen live on the wizard's voice step, 2026-06-10).
 * Past the ceiling we proceed without a token; the request 401s and
 * the normal refresh-retry/error path takes over — a VISIBLE failure
 * instead of an infinite hang, and never a token revocation.
 */
const AUTH_TOKEN_TIMEOUT_MS = 15_000;

const AUTH_TIMED_OUT = Symbol('auth-timeout');

function withAuthTimeout<T>(p: Promise<T>): Promise<T | typeof AUTH_TIMED_OUT> {
  return Promise.race([
    p,
    new Promise<typeof AUTH_TIMED_OUT>((resolve) => {
      setTimeout(() => resolve(AUTH_TIMED_OUT), AUTH_TOKEN_TIMEOUT_MS);
    }),
  ]);
}

/**
 * Resolve the current Cognito ID token, optionally forcing a refresh.
 *
 * Exported so the multipart helper and the SSE reader can attach the
 * same Bearer token as `apiFetch` without duplicating the Amplify call.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  try {
    const session = await withAuthTimeout(
      fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined),
    );
    if (session === AUTH_TIMED_OUT) {
      return null;
    }
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

type RefreshResult =
  | { kind: 'ok'; token: string }
  | { kind: 'dead' } // Cognito says the refresh token is gone — sign out is correct
  | { kind: 'transient' }; // network/throttle — keep the session, retry later

/**
 * Force-refresh the session and classify the outcome.
 *
 * Cognito signals a dead refresh token (revoked, expired, deleted
 * user) with `NotAuthorizedException`; a resolved session with no
 * tokens means there is no signed-in user at all. Everything else —
 * `NetworkError`, fetch TypeErrors, throttling — is transient and must
 * never escalate to a sign-out, because sign-out revokes the refresh
 * token server-side and makes the logout permanent.
 */
async function refreshIdToken(): Promise<RefreshResult> {
  try {
    const session = await withAuthTimeout(fetchAuthSession({ forceRefresh: true }));
    if (session === AUTH_TIMED_OUT) {
      // A hung refresh is indistinguishable from a network stall —
      // transient by definition; never sign out (that would revoke).
      return { kind: 'transient' };
    }
    const token = session.tokens?.idToken?.toString() ?? null;
    return token ? { kind: 'ok', token } : { kind: 'dead' };
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    const dead = name === 'NotAuthorizedException' || name === 'UserUnAuthenticatedException';
    return { kind: dead ? 'dead' : 'transient' };
  }
}

/**
 * Shared 401-recovery step: refresh the session and hand back a fresh
 * token, or throw ApiError(401). Signs the user out ONLY when the
 * session is provably dead; transient refresh failures throw without
 * touching the stored tokens.
 */
async function retryTokenOrThrow(url: string): Promise<string> {
  const refresh = await refreshIdToken();
  if (refresh.kind === 'ok') {
    return refresh.token;
  }
  if (refresh.kind === 'dead') {
    await forceSignOutAndRedirect();
    throw new ApiError(401, url, 'Session expired');
  }
  throw new ApiError(401, url, 'Could not refresh the session — check your connection and retry');
}

/**
 * Sign out and bounce to the Get Started page with a banner reason.
 * Exposed for tests; production callers should never need this.
 */
export async function forceSignOutAndRedirect(): Promise<void> {
  try {
    await signOut();
  } catch {
    // signOut is best-effort; we're navigating away anyway.
  }
  if (typeof window !== 'undefined') {
    window.location.assign(SESSION_EXPIRED_REDIRECT);
  }
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * Client-side watchdog. Abort the request after this many ms and throw
   * ApiError(408). Without it a stalled synchronous backend (e.g. a hung
   * image render) leaves the caller's promise pending forever — the scene
   * tile spins in "loading" with no error and no retry. Mirrors
   * withAuthTimeout's "visible failure beats infinite hang" contract,
   * applied to the request body instead of the token lookup. Omit to keep
   * the request unbounded (default).
   */
  timeoutMs?: number;
}

/**
 * Run `fetch` with an optional abort-after-`timeoutMs` watchdog.
 *
 * Forwards a caller-supplied signal, and translates a watchdog-triggered
 * abort into ApiError(408) so a stalled request surfaces as a retryable
 * error rather than an infinite pending promise. A caller-initiated abort
 * (component unmount) is left to propagate as-is.
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number | undefined,
  callerSignal: AbortSignal | null | undefined,
): Promise<Response> {
  if (!timeoutMs || timeoutMs <= 0) {
    return fetch(url, { ...init, signal: callerSignal ?? init.signal });
  }

  const controller = new AbortController();
  if (callerSignal?.aborted) controller.abort();
  callerSignal?.addEventListener('abort', () => controller.abort(), { once: true });

  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (timedOut) {
      throw new ApiError(408, url, `Request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve an API `path` to the URL `fetch` is called with.
 *
 * Normal dev + production: absolute, against `VITE_API_BASE_URL`.
 *
 * Design-parity tooling: when `VITE_DEV_API_PROXY` is set the bypass
 * dev server proxies `/api` to the backend (see `vite.config.ts`), so
 * we keep the request RELATIVE — that makes it same-origin with the
 * bypass server and sidesteps the backend's :5173-only CORS allowlist,
 * which otherwise hangs every data-driven page in the differ.
 */
export function resolveApiUrl(path: string): string {
  if (env.VITE_DEV_API_PROXY) {
    return path;
  }
  return new URL(path, env.VITE_API_BASE_URL).toString();
}

function buildHeaders(rawBody: unknown, token: string | null, extra?: HeadersInit): HeadersInit {
  const base: Record<string, string> = {
    Accept: 'application/json',
  };
  if (rawBody !== undefined) {
    base['Content-Type'] = 'application/json';
  }
  if (token) {
    base['Authorization'] = `Bearer ${token}`;
  }
  return { ...base, ...(extra ?? {}) };
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const data = (await response.json()) as { error?: string; message?: string };
    return data.error ?? data.message ?? `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

/**
 * Parse a JSON error response into both a message and the machine-readable
 * body (code, required_plan, next_plan, ...). The plan-gate / credit-metering
 * UI reads `ApiError.body` to drive upgrade prompts.
 */
async function parseError(response: Response): Promise<{ message: string; body?: ApiErrorBody }> {
  try {
    const data = (await response.json()) as ApiErrorBody;
    return { message: data.error ?? data.message ?? `HTTP ${response.status}`, body: data };
  } catch {
    return { message: `HTTP ${response.status}` };
  }
}

async function doFetch(
  url: string,
  rest: Omit<ApiRequestInit, 'body'>,
  rawBody: unknown,
  token: string | null,
): Promise<Response> {
  const { timeoutMs, signal: callerSignal, ...fetchRest } = rest;
  const headers = buildHeaders(rawBody, token, fetchRest.headers);
  // `credentials: 'include'` sends the Flask session cookie alongside
  // any Bearer token. In local dev the Cognito config is a placeholder
  // (eu-central-1_LOCAL00000 / local-placeholder-client-id), so Amplify
  // can never mint a real token and the cookie issued by /login is the
  // only auth the API gate accepts. In prod the cookie just rides for
  // free; the Bearer is still the primary credential.
  const init: RequestInit =
    rawBody !== undefined
      ? { ...fetchRest, headers, body: JSON.stringify(rawBody), credentials: 'include' }
      : { ...fetchRest, headers, credentials: 'include' };
  return fetchWithTimeout(url, init, timeoutMs, callerSignal);
}

export async function apiFetch<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const url = resolveApiUrl(path);
  const { body: rawBody, ...rest } = init;

  const initialToken = await getIdToken();
  let response = await doFetch(url, rest, rawBody, initialToken);

  if (response.status === 401) {
    const freshToken = await retryTokenOrThrow(url);
    response = await doFetch(url, rest, rawBody, freshToken);
    if (response.status === 401) {
      await forceSignOutAndRedirect();
      throw new ApiError(401, url, 'Session expired');
    }
  }

  if (!response.ok) {
    const { message, body } = await parseError(response);
    throw new ApiError(response.status, url, message, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Multipart-form variant of `apiFetch`.
 *
 * Used for the wizard endpoints that take file uploads
 * (`ideate-story`, `brainstorm-names`, `save-without-voice`). The
 * browser MUST set the `Content-Type` (it appends the multipart
 * boundary), so we deliberately do NOT set it ourselves, we only
 * attach the `Authorization` header. The 401-refresh-retry contract
 * mirrors `apiFetch` exactly.
 */
export async function apiFetchMultipart<T = unknown>(
  path: string,
  formData: FormData,
  init: Omit<ApiRequestInit, 'body'> = {},
): Promise<T> {
  const url = resolveApiUrl(path);
  const { timeoutMs, signal: callerSignal, ...rest } = init;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // See doFetch · same reason: Flask session cookie is the only
    // working auth in local dev where Cognito is a placeholder.
    return fetchWithTimeout(
      url,
      {
        ...rest,
        method: rest.method ?? 'POST',
        headers,
        body: formData,
        credentials: 'include',
      },
      timeoutMs,
      callerSignal,
    );
  };

  let response = await send(await getIdToken());

  if (response.status === 401) {
    const freshToken = await retryTokenOrThrow(url);
    response = await send(freshToken);
    if (response.status === 401) {
      await forceSignOutAndRedirect();
      throw new ApiError(401, url, 'Session expired');
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, url, await parseErrorMessage(response));
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * POST a multipart form and consume the response as a Server-Sent
 * Events stream.
 *
 * `apiFetch` parses JSON and cannot model a stream, so the wizard's
 * face-preview step uses this. Each SSE frame is `data: <json>\n\n`;
 * we buffer the response body, split on the blank-line delimiter,
 * strip the `data: ` prefix, `JSON.parse` the payload, and hand each
 * parsed frame to `onFrame`. Unparseable frames are skipped silently
 * (a malformed keep-alive should not abort the stream). On a 401 the
 * token is refreshed once and the request retried, matching the
 * `apiFetch` contract.
 *
 * `signal` lets the caller abort the stream (e.g. component unmount).
 *
 * Stall watchdog: if no bytes arrive for `stallTimeoutMs` — including
 * the wait for response headers — the stream is aborted and an
 * `ApiError(408)` is thrown so callers surface a retry instead of an
 * infinite spinner. The backend pings a heartbeat frame every 15s
 * while work is in flight, so the only way to go silent this long is
 * a dead connection or a wedged server thread (the 2026-06-12 wizard
 * freeze: an S3 download hung before the first frame and the UI sat
 * on "Rendering…" forever).
 */
export async function streamSse(
  path: string,
  formData: FormData,
  onFrame: (frame: unknown) => void,
  signal?: AbortSignal,
  stallTimeoutMs = 90_000,
): Promise<void> {
  const url = resolveApiUrl(path);

  // Internal controller so the watchdog can abort the fetch without
  // the caller's signal; the caller's abort is forwarded into it.
  const controller = new AbortController();
  const forwardAbort = (): void => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', forwardAbort, { once: true });

  let stalled = false;
  let stallTimer: ReturnType<typeof setTimeout> | undefined;
  const armWatchdog = (): void => {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => {
      stalled = true;
      controller.abort();
    }, stallTimeoutMs);
  };

  const open = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // See doFetch · same reason: Flask session cookie is the only
    // working auth in local dev where Cognito is a placeholder.
    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
      credentials: 'include',
    });
  };

  try {
    armWatchdog();
    let response = await open(await getIdToken());
    if (response.status === 401) {
      const freshToken = await retryTokenOrThrow(url);
      armWatchdog();
      response = await open(freshToken);
    }
    if (!response.ok || !response.body) {
      throw new ApiError(response.status, url, await parseErrorMessage(response));
    }

    const reader = response.body.getReader();
    // Aborting the fetch controller does not always reject a pending
    // reader.read() (it can simply never settle on some bodies), so
    // the watchdog's abort also cancels the reader directly.
    controller.signal.addEventListener('abort', () => void reader.cancel().catch(() => {}), {
      once: true,
    });
    const decoder = new TextDecoder();
    let buffer = '';

    const flush = (chunk: string): void => {
      buffer += chunk;
      let delimiter = buffer.indexOf('\n\n');
      while (delimiter !== -1) {
        const rawEvent = buffer.slice(0, delimiter);
        buffer = buffer.slice(delimiter + 2);
        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            onFrame(JSON.parse(payload));
          } catch {
            // Skip a malformed frame; the next valid frame still arrives.
          }
        }
        delimiter = buffer.indexOf('\n\n');
      }
    };

    for (;;) {
      armWatchdog();
      const { done, value } = await reader.read();
      if (done) break;
      flush(decoder.decode(value, { stream: true }));
    }
    flush(decoder.decode());
    if (stalled) {
      // reader.cancel() ends the loop with done=true instead of
      // throwing — still a stall, still an error.
      throw new DOMException('Stream stalled', 'AbortError');
    }
  } catch (err) {
    if (stalled) {
      throw new ApiError(
        408,
        url,
        `No data from the server for ${Math.round(stallTimeoutMs / 1000)}s — ` +
          'the connection looks dead. Please retry.',
      );
    }
    throw err;
  } finally {
    clearTimeout(stallTimer);
    signal?.removeEventListener('abort', forwardAbort);
  }
}
